//loader.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LoaderState {
  isLoading: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoaderService {

  public loaderState = new BehaviorSubject<LoaderState>({ isLoading: false });
  private loaderTimeout: any;
  private showTimer: any;
  private hideTimer: any;
  private lastShownAt: number | null = null;
  private pendingMessage?: string;

  // UX timing controls to reduce flicker and eye strain
  private readonly showDelayMs = 150;      // Wait before showing for quick ops
  private readonly minVisibleMs = 500;     // Keep visible briefly once shown

  constructor() { }

  /**
   * Show loader with optional custom message
   * @param message Custom loading message
   */
  show(message?: string) {
    this.pendingMessage = message;

    // If already visible, just update message and refresh auto-hide
    if (this.loaderState.value.isLoading) {
      this.loaderState.next({ isLoading: true, message });
      this.setupAutoHide();
      return;
    }

    // If a show is already scheduled, do nothing (message already stored)
    if (this.showTimer) {
      return;
    }

    // Schedule a delayed show to prevent flash for very short operations
    this.clearHideTimer();
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.lastShownAt = Date.now();
      this.loaderState.next({ isLoading: true, message: this.pendingMessage });
      this.setupAutoHide();
    }, this.showDelayMs);
  }

  /**
   * Hide loader
   */
  hide() {
    // If a show is pending but not yet visible, cancel it
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
      this.pendingMessage = undefined;
      this.clearLoaderTimeout();
      return;
    }

    // If not visible, nothing to do
    if (!this.loaderState.value.isLoading) {
      this.clearLoaderTimeout();
      return;
    }

    const now = Date.now();
    const elapsed = this.lastShownAt ? now - this.lastShownAt : this.minVisibleMs;

    // Respect minimum visible time to avoid flicker during quick successive requests
    if (elapsed < this.minVisibleMs) {
      this.clearHideTimer();
      this.hideTimer = setTimeout(() => {
        this.performHide();
      }, this.minVisibleMs - elapsed);
    } else {
      this.performHide();
    }
  }

  /**
   * Update loading message without changing loading state
   * @param message New loading message
   */
  updateMessage(message: string) {
    const currentState = this.loaderState.value;
    if (currentState.isLoading) {
      this.loaderState.next({ ...currentState, message });
    } else if (this.showTimer) {
      // Update pending message if show has been scheduled but not yet visible
      this.pendingMessage = message;
    }
  }

  /**
   * Show loader for specific operations with predefined messages
   */
  showForOperation(operation: 'saving' | 'loading' | 'deleting' | 'uploading' | 'processing' | 'verifying' | 'creating') {
    const messages = {
      saving: 'Saving changes',
      loading: 'Loading data',
      deleting: 'Deleting item',
      uploading: 'Uploading file',
      processing: 'Processing request',
      verifying: 'Verifying details',
      creating: 'Creating account'
    };

    this.show(messages[operation]);
  }

  /**
   * Get current loader state
   */
  getState(): LoaderState {
    return this.loaderState.value;
  }

  /**
   * Force reset loader state (emergency method)
   */
  forceReset() {
    this.clearLoaderTimeout();
    this.clearShowTimer();
    this.clearHideTimer();
    this.lastShownAt = null;
    this.pendingMessage = undefined;
    this.loaderState.next({ isLoading: false });
  }

  /**
   * Clear the loader timeout
   */
  private clearLoaderTimeout() {
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }
  }

  /**
   * Set or refresh the auto-hide safety timeout
   */
  private setupAutoHide() {
    this.clearLoaderTimeout();
    this.loaderTimeout = setTimeout(() => {
      this.performHide();
    }, 30000);
  }

  private performHide() {
    this.clearHideTimer();
    this.clearLoaderTimeout();
    this.lastShownAt = null;
    this.pendingMessage = undefined;
    this.loaderState.next({ isLoading: false });
  }

  private clearShowTimer() {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
